import { useState } from "react";
import type { FormEvent } from "react";

import { isAPIError } from "@/core/services";
import { useAsyncList, useDialogForm, useToast } from "@/ds";
import {
  criarEscopoPadrao,
  editarEscopoPadrao,
  getEquipamentosEscopoPadrao,
} from "@/modules/financeiro-configuracoes";
import type {
  EquipamentoOpcao,
  EscopoPadraoLinha,
  PeriodicidadeEscopoPadrao,
} from "@/modules/financeiro-configuracoes";
import { useChaveIdempotencia, useConfirmarDescarte } from "@/shared/hooks";
import {
  ESCOPOS_PADRAO_ERROS,
  ESCOPOS_PADRAO_TOASTS,
  rotuloVersao,
} from "../../escopos-padrao.constants";
import { gruposDaVisao } from "../../escopos-padrao.filtros";
import {
  APOIO_POR_VISAO,
  EMBARCACAO_LABEL,
  ESCOPO_LABEL,
  SALVAR_EDICAO_LABEL,
  SALVAR_LABEL,
  ESCOPO_MAX,
  ESCOPO_PLACEHOLDER,
  ESCOPO_PADRAO_FORM_ID,
  FABRICANTE_LABEL,
  GRUPO_LABEL,
  MODELO_LABEL,
  NOME_ESCOPO_LABEL,
  MSG_EMBARCACAO_OBRIGATORIA,
  MSG_EQUIPAMENTO_JA_EXISTE,
  MSG_EQUIPAMENTO_OBRIGATORIO,
  MSG_ESCOPO_JA_EXISTE,
  MSG_ESCOPO_OBRIGATORIO,
  MSG_GRUPO_OBRIGATORIO,
  MSG_TIPO_OBRIGATORIO,
  SECAO_CUSTO,
  SECAO_CUSTO_POR_PERIODICIDADE,
  SECAO_IDENTIFICACAO,
  SEM_VALOR,
  SUBGRUPO_LABEL,
  TIPO_OBRA_LABEL,
  EQUIPAMENTO_LABEL,
  TITULO_EDICAO,
  TITULO_INTERMEDIARIA,
  TITULO_NOVO,
  TITULO_OVERHAUL,
  avisoEdicao,
} from "./escopo-padrao-dialog.constants";
import {
  FORM_VAZIO,
  algumErroDeCusto,
  errosDeIdentificacao,
  errosDoCusto,
  formDaLinha,
  montarCorpoEdicao,
  montarCorpoNovo,
  periodicidadesDaVisao,
  visaoDoTipo,
  visaoTemGrupo,
} from "./escopo-padrao-dialog.rules";
import type { CustoForm, EscopoPadraoForm } from "./escopo-padrao-dialog.rules";
import type {
  CampoBloqueado,
  CampoNumero,
  CampoSelecao,
  EscopoPadraoDialogProps,
  EscopoPadraoDialogViewModel,
  SecaoCusto,
} from "./types-escopo-padrao-dialog";

type CampoDoServidor = "tipoObraId" | "embarcacaoId" | "grupoId" | "equipamentoId" | "escopo";

const CAMPO_POR_MENSAGEM: Readonly<Record<string, CampoDoServidor>> = {
  [MSG_TIPO_OBRIGATORIO]: "tipoObraId",
  [MSG_EMBARCACAO_OBRIGATORIA]: "embarcacaoId",
  [MSG_GRUPO_OBRIGATORIO]: "grupoId",
  [MSG_EQUIPAMENTO_OBRIGATORIO]: "equipamentoId",
  [MSG_ESCOPO_OBRIGATORIO]: "escopo",
};

const TITULO_POR_PERIODICIDADE: Record<PeriodicidadeEscopoPadrao, string | null> = {
  INTERMEDIARIA: TITULO_INTERMEDIARIA,
  OVERHAUL: TITULO_OVERHAUL,
  UNICA: null,
};

const textoOuTraco = (texto: string | null | undefined) => (texto?.trim() ? texto : SEM_VALOR);

const bloqueado = (rotulo: string, valor: string): CampoBloqueado => ({
  rotulo,
  valor,
  testId: `escopo-padrao-bloqueado-${rotulo}`,
});

function bloqueadosAntesDoAno(escopo: EscopoPadraoLinha): CampoBloqueado[] {
  const campos: CampoBloqueado[] = [
    bloqueado(TIPO_OBRA_LABEL, `${escopo.tipoObra.codigo} · ${escopo.tipoObra.nome}`),
  ];
  if (escopo.grupo) campos.push(bloqueado(GRUPO_LABEL, escopo.grupo.nome));
  if (escopo.subgrupo) campos.push(bloqueado(SUBGRUPO_LABEL, escopo.subgrupo.nome));
  return campos;
}

function bloqueadosDepoisDoAno(escopo: EscopoPadraoLinha): CampoBloqueado[] {
  const campos: CampoBloqueado[] = [
    bloqueado(EMBARCACAO_LABEL, textoOuTraco(escopo.embarcacao?.nome)),
  ];
  if (escopo.equipamento) {
    campos.push(
      bloqueado(EQUIPAMENTO_LABEL, escopo.equipamento.tag),
      bloqueado(FABRICANTE_LABEL, textoOuTraco(escopo.equipamento.fabricante)),
      bloqueado(MODELO_LABEL, textoOuTraco(escopo.equipamento.modelo)),
    );
  } else {
    campos.push(
      bloqueado(
        escopo.visao === "CLASSE" ? ESCOPO_LABEL : NOME_ESCOPO_LABEL,
        textoOuTraco(escopo.escopo),
      ),
    );
  }
  return campos;
}

export function useEscopoPadraoDialog({
  versao,
  escopo,
  tipos,
  estrutura,
  embarcacoes,
  moedas,
  onClose,
  onConcluido,
}: EscopoPadraoDialogProps): EscopoPadraoDialogViewModel {
  const editando = escopo !== undefined;
  const toast = useToast();
  const { comChave } = useChaveIdempotencia();
  const [tentou, setTentou] = useState(false);
  const [errosServidor, setErrosServidor] = useState<Partial<Record<CampoDoServidor, string>>>({});

  const visaoDoCodigo = (tipoObraId: string | null) => {
    const tipo = tipos.find((item) => item.id === tipoObraId);
    return tipo ? visaoDoTipo(tipo.codigo) : null;
  };

  const form = useDialogForm<EscopoPadraoForm>({
    initial: () => (escopo ? formDaLinha(escopo) : FORM_VAZIO),
    validate: (valores) => {
      const visaoDoForm = escopo?.visao ?? visaoDoCodigo(valores.tipoObraId);
      if (!visaoDoForm) return { tipoObraId: errosDeIdentificacao(valores, null).tipoObraId };
      return {
        ...(editando ? {} : errosDeIdentificacao(valores, visaoDoForm)),
        ...(algumErroDeCusto(valores, visaoDoForm) ? { custos: "Revise os custos." } : {}),
      };
    },
    onSubmit: async (valores) => {
      const visaoDoForm = escopo?.visao ?? visaoDoCodigo(valores.tipoObraId);
      if (!visaoDoForm) return;
      setErrosServidor({});
      try {
        if (escopo) {
          const corpo = montarCorpoEdicao(valores, visaoDoForm);
          await comChave(corpo, (idempotencyKey) =>
            editarEscopoPadrao(escopo.id, corpo, { idempotencyKey }),
          );
        } else {
          const corpo = montarCorpoNovo(valores, visaoDoForm);
          if (!corpo) return;
          await comChave(corpo, (idempotencyKey) => criarEscopoPadrao(corpo, { idempotencyKey }));
        }
      } catch (erro) {
        if (isAPIError(erro) && erro.status === 409) {
          const campo =
            erro.message === MSG_EQUIPAMENTO_JA_EXISTE
              ? "equipamentoId"
              : erro.message === MSG_ESCOPO_JA_EXISTE
                ? "escopo"
                : null;
          if (campo) {
            setErrosServidor({ [campo]: erro.message });
            return;
          }
        }
        const recusa = isAPIError(erro) && erro.status === 400 ? erro : null;
        const campoDaRecusa = recusa ? CAMPO_POR_MENSAGEM[recusa.message] : undefined;
        if (recusa && campoDaRecusa) {
          setErrosServidor({ [campoDaRecusa]: recusa.message });
          return;
        }
        throw erro;
      }
      toast.success(
        editando ? ESCOPOS_PADRAO_TOASTS.custoAtualizado : ESCOPOS_PADRAO_TOASTS.escopoCriado,
      );
      onConcluido();
    },
  });

  const { values } = form;
  const visao = escopo?.visao ?? visaoDoCodigo(values.tipoObraId);

  const descarte = useConfirmarDescarte({
    onDescartar: onClose,
    testId: "escopo-padrao-descarte",
  });

  const mostrarErros = tentou || form.hasErrors;
  const erros = form.errors as Partial<Record<string, string>>;
  const erroDoCampo = (campo: CampoDoServidor) => erros[campo] ?? errosServidor[campo] ?? null;
  const limpaServidor = (campo: CampoDoServidor) =>
    setErrosServidor((atuais) => ({ ...atuais, [campo]: undefined }));

  const buscaEquipamentos =
    !editando && visao === "EQUIPAMENTOS" && !!values.embarcacaoId && !!values.grupoId;
  const equipamentos = useAsyncList<
    { embarcacaoId: string; grupoEscopoMacroId: string },
    EquipamentoOpcao
  >({
    enabled: buscaEquipamentos,
    fetcher: async (params, signal) => {
      const items = await getEquipamentosEscopoPadrao(params, { signal });
      return { items, total: items.length };
    },
    params: {
      embarcacaoId: values.embarcacaoId ?? "",
      grupoEscopoMacroId: values.grupoId ?? "",
    },
    forbiddenMessage: ESCOPOS_PADRAO_ERROS.equipamentosSemPermissao,
    errorMessage: ESCOPOS_PADRAO_ERROS.equipamentos,
  });
  const equipamentoEscolhido = buscaEquipamentos
    ? equipamentos.items.find((item) => item.id === values.equipamentoId)
    : undefined;

  const seletor = (
    campo: CampoDoServidor,
    chave: "tipoObraId" | "embarcacaoId" | "grupoId" | "equipamentoId",
    opcoes: CampoSelecao["opcoes"],
    testId: string,
    aoEscolher: (valor: string | null) => Partial<EscopoPadraoForm>,
  ): CampoSelecao => ({
    valor: values[chave],
    opcoes,
    erro: erroDoCampo(campo),
    testId,
    escolher: (valor) => {
      limpaServidor(campo);
      form.setValues({ ...values, [chave]: valor, ...aoEscolher(valor) });
    },
  });

  const tiposOpcoes = tipos
    .filter((tipo) => tipo.situacao === "ATIVO" && visaoDoTipo(tipo.codigo))
    .map((tipo) => ({ label: `${tipo.codigo} · ${tipo.nome}`, value: tipo.id }));

  const campoNumero = (
    periodicidade: PeriodicidadeEscopoPadrao,
    chave: keyof CustoForm,
    erro: string | null,
    testId: string,
  ): CampoNumero => ({
    valor: values[periodicidade][chave] as number | null,
    erro: mostrarErros ? erro : null,
    testId,
    digitar: (valor) => form.setValue(periodicidade, { ...values[periodicidade], [chave]: valor }),
  });
  const campoMoeda = (
    periodicidade: PeriodicidadeEscopoPadrao,
    chave: "moedaServicosId" | "moedaMateriaisId",
    erro: string | null,
    testId: string,
  ): CampoSelecao => ({
    valor: values[periodicidade][chave],
    obrigatorio:
      values[periodicidade][chave === "moedaServicosId" ? "valorServicos" : "valorMateriais"] !==
      null,
    opcoes: moedas.map((moeda) => ({ label: `${moeda.codigo} · ${moeda.nome}`, value: moeda.id })),
    erro: mostrarErros ? erro : null,
    testId,
    escolher: (valor) => form.setValue(periodicidade, { ...values[periodicidade], [chave]: valor }),
  });

  const custos: SecaoCusto[] = visao
    ? periodicidadesDaVisao(visao).map((periodicidade) => {
        const falhas = errosDoCusto(values[periodicidade], visao === "EQUIPAMENTOS");
        const base = `escopo-padrao-${periodicidade.toLowerCase()}`;
        return {
          periodicidade,
          testId: `escopo-padrao-custo-${periodicidade}`,
          titulo: TITULO_POR_PERIODICIDADE[periodicidade],
          horas:
            visao === "EQUIPAMENTOS"
              ? campoNumero(periodicidade, "horas", falhas.horas, `${base}-horas`)
              : null,
          servicos: {
            valor: campoNumero(
              periodicidade,
              "valorServicos",
              falhas.valorServicos,
              `${base}-servicos`,
            ),
            moeda: campoMoeda(
              periodicidade,
              "moedaServicosId",
              falhas.moedaServicosId,
              `${base}-moeda-servicos`,
            ),
          },
          materiais: {
            valor: campoNumero(
              periodicidade,
              "valorMateriais",
              falhas.valorMateriais,
              `${base}-materiais`,
            ),
            moeda: campoMoeda(
              periodicidade,
              "moedaMateriaisId",
              falhas.moedaMateriaisId,
              `${base}-moeda-materiais`,
            ),
          },
        };
      })
    : [];

  const enviar = (evento: FormEvent<HTMLFormElement>) => {
    evento.preventDefault();
    setTentou(true);
    void form.submit();
  };

  const rotuloEscopo = visao === "CLASSE" ? ESCOPO_LABEL : NOME_ESCOPO_LABEL;
  const dadoDoEquipamento = (item: EquipamentoOpcao | undefined, campo: "fabricante" | "modelo") =>
    !editando && visao === "EQUIPAMENTOS" ? (item ? textoOuTraco(item[campo]) : "") : null;
  const comEscopo = !editando && (visao === "CLASSE" || visao === "OUTROS");
  const grupos = gruposDaVisao(estrutura, visao ?? "OUTROS", values.tipoObraId ?? "").map(
    ({ label, value }) => ({ label, value }),
  );

  return {
    formId: ESCOPO_PADRAO_FORM_ID,
    titulo: editando ? TITULO_EDICAO : TITULO_NOVO,
    salvarLabel: editando ? SALVAR_EDICAO_LABEL : SALVAR_LABEL,
    anoReferencia: rotuloVersao(versao),
    aviso: escopo ? avisoEdicao(rotuloVersao(versao), escopo.uso.escopos, escopo.uso.obras) : null,
    bloqueados: escopo ? bloqueadosAntesDoAno(escopo) : [],
    bloqueadosApos: escopo ? bloqueadosDepoisDoAno(escopo) : [],
    secoes: {
      identificacao: SECAO_IDENTIFICACAO,
      custo: visao
        ? visao === "EQUIPAMENTOS"
          ? SECAO_CUSTO_POR_PERIODICIDADE
          : SECAO_CUSTO
        : null,
    },
    apoio: !editando && visao ? APOIO_POR_VISAO[visao] : null,
    tipo: editando
      ? null
      : seletor("tipoObraId", "tipoObraId", tiposOpcoes, "escopo-padrao-tipo", () => ({
          grupoId: null,
          equipamentoId: null,
          escopo: "",
          INTERMEDIARIA: FORM_VAZIO.INTERMEDIARIA,
          OVERHAUL: FORM_VAZIO.OVERHAUL,
          UNICA: FORM_VAZIO.UNICA,
        })),
    embarcacao:
      editando || !visao
        ? null
        : seletor(
            "embarcacaoId",
            "embarcacaoId",
            embarcacoes.map(({ id, nome }) => ({ label: nome, value: id })),
            "escopo-padrao-embarcacao",
            () => ({ equipamentoId: null }),
          ),
    grupo:
      editando || !visaoTemGrupo(visao)
        ? null
        : seletor("grupoId", "grupoId", grupos, "escopo-padrao-grupo", () => ({
            equipamentoId: null,
          })),
    equipamento:
      editando || visao !== "EQUIPAMENTOS"
        ? null
        : {
            ...seletor(
              "equipamentoId",
              "equipamentoId",
              buscaEquipamentos
                ? equipamentos.items.map((item) => ({
                    label: item.tag,
                    value: item.id,
                    disabled: item.tagJaCadastrada,
                  }))
                : [],
              "escopo-padrao-equipamento",
              () => ({}),
            ),
            aguardando: !buscaEquipamentos,
            carregando: buscaEquipamentos && equipamentos.isLoading,
          },
    fabricante: dadoDoEquipamento(equipamentoEscolhido, "fabricante"),
    modelo: dadoDoEquipamento(equipamentoEscolhido, "modelo"),
    escopo: comEscopo
      ? {
          rotulo: rotuloEscopo,
          valor: values.escopo,
          max: ESCOPO_MAX,
          placeholder: ESCOPO_PLACEHOLDER[visao === "CLASSE" ? "CLASSE" : "OUTROS"],
          erro: erroDoCampo("escopo"),
          testId: "escopo-padrao-escopo",
          digitar: (valor) => {
            limpaServidor("escopo");
            form.setValue("escopo", valor);
          },
        }
      : null,
    custos,
    isSubmitting: form.isSubmitting,
    submitError: form.submitError,
    enviar,
    cancelar: () => descarte.pedir(form.isDirty),
    descarte: descarte.confirmacao,
  };
}
